import { datadogLogs, Logger } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";

import { getRuntimeEnvironment } from "./getRuntimeEnvironment";

const isProductionBuild = import.meta.env.MODE === "production";
const environment = getRuntimeEnvironment();

// RUM config, carried over from login-ui!222 (2025-02-07 — reverted six days
// later in !227 after DE POST /passwordless/email/ started 415ing). The
// leading theory was `allowedTracingUrls` injecting trace headers that broke
// Content-Type; that path is not exercised here (option omitted). See MR
// !282 for the full rationale.
//
// Keeping the per-env config explicit rather than ternary-per-field so
// flipping the kill-switch to include production doesn't silently promote
// staging defaults (100% sample rate, etc.) onto a public high-traffic page.
type RumEnvironmentConfig = {
  applicationId: string;
  clientToken: string;
  env: string;
  sessionSampleRate: number;
};

const RUM_CONFIG_BY_ENVIRONMENT: Record<
  ReturnType<typeof getRuntimeEnvironment>,
  RumEnvironmentConfig
> = {
  production: {
    applicationId: "23dc4339-c527-4bd0-a7d7-d8d11c7a8026",
    clientToken: "pubc296c5cdf9541efc5f360dd77e7be746",
    // Matches the env tag on the DD_LOGS init in prod/{uk,de}/index.html so
    // logs and RUM correlate on the same session.
    env: "production",
    // Public login page — start conservative on the flip and ramp separately.
    sessionSampleRate: 10,
  },
  staging: {
    applicationId: "e4e2c1aa-cd18-4ecd-9b8d-e73dee179e97",
    clientToken: "pub6175b0f8bbecb2be93245c1e885bee9a",
    env: "staging",
    sessionSampleRate: 100,
  },
};

// Kill-switch. Flip to `["production", "staging"]` once stage has been clean
// for a couple of days. The @datadog/browser-rum module has import-time side
// effects (defines window.DD_RUM), and prod bundles cannot tree-shake the
// SDK away because `datadogRum.init` is referenced at runtime — so the SDK
// ships to prod regardless of this array. A future refactor removing this
// guard would silently activate RUM in prod; keep the gate.
const RUM_ENABLED_ENVIRONMENTS: readonly ReturnType<
  typeof getRuntimeEnvironment
>[] = ["staging"];

if (isProductionBuild) {
  datadogLogs.init({
    clientToken:
      environment === "production"
        ? "pubc296c5cdf9541efc5f360dd77e7be746"
        : "pub6175b0f8bbecb2be93245c1e885bee9a",
    site: "datadoghq.com",
    service: "login-ui",
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
    env: environment,
    version: import.meta.env.VITE_RELEASE,
  });

  if (RUM_ENABLED_ENVIRONMENTS.includes(environment)) {
    const rumConfig = RUM_CONFIG_BY_ENVIRONMENT[environment];
    datadogRum.init({
      applicationId: rumConfig.applicationId,
      clientToken: rumConfig.clientToken,
      site: "datadoghq.com",
      service: "login-ui",
      env: rumConfig.env,
      version: import.meta.env.VITE_RELEASE,
      sessionSampleRate: rumConfig.sessionSampleRate,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      // Login flow puts email / password / TOTP into inputs; mask them from
      // Session Replay serialisation and — combined with
      // enablePrivacyForActionName below — from click action-name capture.
      defaultPrivacyLevel: "mask-user-input",
      // Without this, trackUserInteractions sends element text / aria-label
      // as `action.target.name`, which leaks PII from any dynamically-
      // labelled element the user clicks (autofill suggestions, list rows).
      // The SDK's own config comment marks this as the intended next-major
      // default; setting it now is forward-compatible.
      enablePrivacyForActionName: true,
    });
  }
}

export default (isProductionBuild
  ? datadogLogs.logger
  : { ...console }) as Logger;
